import { removeEmoji, removeMarkdown } from '@Sage/ssm-utils';

const getLatestPhrase = (loadingPhrase) => {
    if (Array.isArray(loadingPhrase)) {
        return loadingPhrase.at(-1) ?? '';
    }

    if (typeof loadingPhrase === 'string') {
        return loadingPhrase;
    }

    return '';
};

const getDisplayPhrase = ({ initialPhrase, loadingPhrase, stripEmoji, stripMarkdown }) => {
    let phrase = getLatestPhrase(loadingPhrase) || initialPhrase || '';

    if (stripMarkdown) {
        phrase = removeMarkdown(phrase);
    }

    return stripEmoji ? removeEmoji(phrase) : phrase;
};

/**
 * Returns the current loading phrase from the latest phrase received.
 * The initial phrase is used as a fallback until the first loading phrase arrives.
 *
 * @param {Object} options - Configuration options
 * @param {boolean} options.isLoading - Whether the loading state is active
 * @param {string} [options.initialPhrase] - Optional phrase to always show first when loading starts
 * @param {Array<string>|string} [options.loadingPhrase] - Latest loading phrase, or phrase history during migration
 * @param {boolean} [options.stripEmoji=false] - Whether emoji characters should be removed from the returned phrase
 * @param {boolean} [options.stripMarkdown=false] - Whether markdown formatting should be removed from the returned phrase
 * @returns {string} Current phrase to display, or empty string if not loading
 *
 * @example
 * const currentPhrase = useLoadingPhrases({
 *     initialPhrase: 'Starting up...',
 *     isLoading: true,
 *     loadingPhrase: 'Checking sources...',
 *     stripEmoji: true,
 *     stripMarkdown: true,
 * });
 * // Returns: 'Checking sources...'
 */
const useLoadingPhrases = ({
    initialPhrase,
    isLoading,
    loadingPhrase,
    stripEmoji = false,
    stripMarkdown = false,
}) => {
    if (!isLoading) {
        return '';
    }

    return getDisplayPhrase({ initialPhrase, loadingPhrase, stripEmoji, stripMarkdown });
};

export default useLoadingPhrases;
